import { Component, EventEmitter, Output } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, AbstractControl, ValidationErrors, Validators } from '@angular/forms';
import { BusinessService } from '@app/business/business.service';
import { InfoService } from '@app/core/info/info.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject, switchMap, takeUntil, takeWhile, timer } from 'rxjs';
import { AppConfig } from '@app/config/app.config';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { SigningService } from '@app/certificates/signing/signing.service';
import { SessionStorageService } from '@app/core/local-storage/local-storage.service';
import { Router } from '@angular/router';

@Component({
  selector: 'cloud-managment-hardware-certificates',
  templateUrl: './managment-hardware-certificates.component.html',
  styleUrls: ['./managment-hardware-certificates.component.scss']
})
export class ManagmentHardwareCertificatesComponent {
  @Output() close: EventEmitter<any> = new EventEmitter();
  @Output() lockDetailsForm = new EventEmitter<boolean>();

  managementForm: FormGroup;
  rowData: any;
  loaded: boolean = false;
  translatedActionListValid: { name: string, value: string }[] = [];
  translatedActionListExpired: { name: string, value: string }[] = [];
  actionListExpired: string[] = ['RESTORE', 'REVOKE'];
  actionListValid: string[] = ['HOLD', 'REVOKE'];

  requestData: any;
  attachFile = false;
  manageApplication = false;
  hideGenerateApp = false;
  isApplicationGenerated = false;
  hideGenerateFile = true;
  @Output() isDocumentGenerated = new EventEmitter<boolean>();
  loading = false;

  maxUploadSize = 5242880;
  timer: NodeJS.Timer;
  timeLeft: number;
  unsubscribe$: Subject<void> = new Subject<void>();
  clientDocument;
  gotFile = false;
  hasFileForUpload = false;
  documentType;
  base64Image = '';
  pdfSrc;
  applicationUrl: SafeUrl;
  validity = 10080;
  showError: boolean = false
  resMessage: string;
  disableCancel: boolean = false;
  requestCompleted = false;

  constructor(
    private formBuilder: FormBuilder,
    public businessService: BusinessService,
    public info: InfoService,
    private sanitizer: DomSanitizer,
    private translateService: TranslateService,
    private sessionStorageService: SessionStorageService,
    private router: Router,
  ) { }

  goBack(): void {
    this.router.navigate(['/business/hardwareCertificates'], { state: { fromManagePage: true } });
  }

  ngOnInit(): void {
    this.initForm();
    this.prefillRequestorFields();
    this.rowData = this.businessService.getStoredRow();
    if (this.rowData) {
      this.loaded = true;
      const expiredKeys = this.actionListExpired.map(action => `certificates.certificateActions.${action}`);
      const validKeys = this.actionListValid.map(action => `certificates.certificateActions.${action}`);
      this.translateService.get(expiredKeys).subscribe(translations => {
        this.translatedActionListExpired = this.actionListExpired.map(action => ({
          name: translations[`certificates.certificateActions.${action}`],
          value: action
        }));
      });

      this.translateService.get(validKeys).subscribe(translations => {
        this.translatedActionListValid = this.actionListValid.map(action => ({
          name: translations[`certificates.certificateActions.${action}`],
          value: action
        }));
      });
    }
  }
  initForm() {
    this.managementForm = this.formBuilder.group({
      revokeAction: new FormControl('', Validators.required),
      requestorName: new FormControl('', Validators.required),
      requestorContact: new FormControl('', [Validators.required, this.emailOrPhoneValidator]),
      requestorRole: new FormControl('', Validators.required),
      requestReason: new FormControl('', Validators.required),
    });
  }

  private prefillRequestorFields(): void {
    const loginEventData = this.sessionStorageService.getItem('loginEventData');
    const userData = loginEventData?.userData || loginEventData || {};

    const currentLang = this.translateService.currentLang || 'bg';
    const firstName = typeof userData.firstName === 'object'
      ? userData.firstName[currentLang] || userData.firstName.bg || userData.firstName.en
      : userData.firstName;
    const lastName = typeof userData.lastName === 'object'
      ? userData.lastName[currentLang] || userData.lastName.bg || userData.lastName.en
      : userData.lastName;
    const requestorName = [firstName, lastName].filter(Boolean).join(' ').trim();

    const requestorContact = userData.currentCertificateEmail || userData.email || userData.mail || '';

    this.managementForm.patchValue({
      requestorName,
      requestorContact
    });
  }

  emailOrPhoneValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return { required: true };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    const phoneRegex = /^\+?[0-9]{7,15}$/;

    const isValidEmail = emailRegex.test(value);
    const isValidPhone = phoneRegex.test(value);

    return isValidEmail || isValidPhone ? null : { emailOrPhone: true };
  }

  submitManagementForm() {
    this.disableCancel = true;
    this.resMessage = this.translateService.instant('businessTexts.messegeRequestBeing');
    this.showError = false;
    this.loading = true;

    if (this.managementForm.invalid) {
      this.resMessage = this.translateService.instant('businessTexts.formInvalid');
      this.disableCancel = false;
      this.loading = false;
      return;
    }

    this.businessService.createAndProcessCqesManagementRequest(
      this.rowData,
      this.managementForm.value
    ).subscribe({
      next: (response: any) => {
        if (response?.status === 200 || response?.status === 202) {
          sessionStorage.setItem('btrustRevokeRequestId', JSON.stringify(response.body.btrustRevokeRequestId));
          this.resMessage = this.translateService.instant('businessTexts.managementRequestProcessed');
          this.showError = false;
          this.disableCancel = true;
          this.processManagementResponse(response);
        } else {
          this.handleManagementRequestError(response?.message);
        }
      },
      error: (err) => {
        this.handleManagementRequestError(err?.error?.message || err?.message);
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  private handleManagementRequestError(message?: string): void {
    this.resMessage = message || this.translateService.instant('businessTexts.submitRequestAgain');
    this.showError = true;
    this.disableCancel = false;
    this.loading = false;
    this.managementForm.reset();
  }

  private processManagementResponse(response: any): void {
    const signedData = response.body.signatureData.signature;

    const fileContent = response.body.fileContent;
    const pdfBlob = new Blob([Buffer.from(fileContent, 'base64')], { type: 'application/pdf' });
    const uploadBlob = new Blob([fileContent], { type: 'multipart/form-data' });

    const contentDisposition = response.headers.get('Content-Disposition');
    const matches = /filename="([^"]+)"/.exec(contentDisposition);
    const fileName = matches && matches[1] ? matches[1] : 'Request_for_management.pdf';

    const fileURL = URL.createObjectURL(pdfBlob);

    const link = document.createElement('a');
    this.applicationUrl = this.sanitizer.bypassSecurityTrustUrl(fileURL);
    link.href = fileURL;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();

    this.attachFile = true;

    const fileForUpload = new File([uploadBlob], fileName, { type: 'application/pdf' });
    this.businessService.completeCqesManagementRequest(this.rowData, fileForUpload, fileName, signedData)
      .subscribe({
        next: (res: any) => {
          if (res?.status === 200) {
            this.showError = false;
            this.loading = false;
            this.disableCancel = false;
            this.requestCompleted = true;
            this.resMessage = this.translateService.instant('businessTexts.certificateStatusChanged');
            setTimeout(() => {
              this.router.navigate(['/business/hardwareCertificates'], {
                state: { fromSubmitManagementForm: true }
              });
            }, 2000);
          } else {
            this.handleManagementRequestError(res?.message);
          }
        },
        error: (err) => {
          this.handleManagementRequestError(err?.error?.message || err?.message);
        },
        complete: () => {
          this.loading = false;
        }
      });
  }

  setSigners(userData): any[] {
    return [{
      'cmrId': userData.cmrId,
      'sequenceIndex': '1',
      'timeToSign': this.validity
    }];
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}